import React from 'react';
import { useSettings } from '../../context/SettingsContext';
import SectionHeader from '../common/SectionHeader';
import SectionNavigation from '../common/SectionNavigation';

function GlobalSettingsForm({ onNextSection, onPreviousSection, currentSectionIndex, sectionNames, isEmbedded = false }) {
  const { globalSettings, handleGlobalSettingChange, expandedSections } = useSettings();
  const currentYear = new Date().getFullYear();

  const handleNumericChange = (field, value) => {
    // Allow empty string temporarily, parse to 0 if left empty or invalid
    if (value === '') {
        handleGlobalSettingChange(field, ''); // Keep empty for user input
    } else {
        const parsed = parseFloat(value);
        handleGlobalSettingChange(field, isNaN(parsed) ? 0 : parsed); // Default to 0 if invalid
    }
  };

   const handleBlurNumeric = (field, value) => {
       // On blur, if the input is empty, set it back to 0 or a default
       if (value === '') {
            handleGlobalSettingChange(field, 0);
       }
   }

  const handleIntegerChange = (field, value) => {
     if (value === '') {
        handleGlobalSettingChange(field, '');
     } else {
        const parsed = parseInt(value);
        handleGlobalSettingChange(field, isNaN(parsed) ? currentYear + 10 : parsed); // Default if invalid
     }
  }

   const handleBlurInteger = (field, value, defaultValue) => {
       if (value === '') {
            handleGlobalSettingChange(field, defaultValue);
       }
   }

  const handleCheckboxChange = (field, checked) => {
    handleGlobalSettingChange(field, checked);
  };


  return (
    <div className="calculator-section">
      {!isEmbedded && (
        <SectionHeader title="Global Calculation Settings" sectionKey="settings" />
      )}

      <div className="calculator-form" id="section-content-settings">
          <div className="form-row">
             <div className="form-group compact">
                <label htmlFor="annual-cola">Annual COLA (%) (Post-Retirement):
                  <div className="tooltip" style={{display: 'inline-block', marginLeft: '5px'}}>
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">Over the past 20 years (2005–2025), the average annual Cost-of-Living Adjustment (COLA) for FERS retirees has been approximately 1.72%.</span>
                  </div>
                </label>
                <input
                  type="number" id="annual-cola"
                  value={globalSettings.annualCola}
                  onChange={(e) => handleNumericChange('annualCola', e.target.value)}
                   onBlur={(e) => handleBlurNumeric('annualCola', e.target.value)} // Reset to 0 if left blank
                  step="0.1" min="0" max="15"
                  className="number-input"
                  aria-label="Annual Cost of Living Adjustment Percentage after retirement"
                  placeholder="e.g., 2.0"
                />
                 <span className="help-text">For Pension, Supplement, SS (approx).</span>
              </div>
            <div className="form-group compact">
                <label htmlFor="cola-while-employed">Annual Pay Increase (%) (Pre-Retirement):
                  <div className="tooltip" style={{display: 'inline-block', marginLeft: '5px'}}>
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">Over the past 20 years (2005–2025), active General Schedule (GS) federal employees have received an average annual pay raise of approximately 2.1%, encompassing both base pay and locality adjustments.</span>
                  </div>
                </label>
                <input
                    type="number" id="cola-while-employed"
                    value={globalSettings.colaWhileEmployed}
                    onChange={(e) => handleNumericChange('colaWhileEmployed', e.target.value)}
                    onBlur={(e) => handleBlurNumeric('colaWhileEmployed', e.target.value)}
                    step="0.1" min="0" max="15"
                    className="number-input"
                    aria-label="Assumed Annual Pay Increase Percentage while employed"
                    placeholder="e.g., 2.1"
                />
                 <span className="help-text">General pay increase if scenario COLA off.</span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group compact">
              <label htmlFor="pre-ret-growth">TSP Growth Rate (Pre-Ret) (%):
                <div className="tooltip" style={{display: 'inline-block', marginLeft: '5px'}}>
                  <span className="tooltip-icon">?</span>
                  <span className="tooltip-text">Conservative planning assumption of 7.5-8.5% annual TSP growth. Based on historical mixed portfolio returns with downward adjustment for volatility. Actual returns will vary significantly year-to-year.</span>
                </div>
              </label>
              <input
                type="number" id="pre-ret-growth"
                value={globalSettings.preRetirementGrowth}
                onChange={(e) => handleNumericChange('preRetirementGrowth', e.target.value)}
                onBlur={(e) => handleBlurNumeric('preRetirementGrowth', e.target.value)}
                step="0.5" min="-10" max="25"
                className="number-input"
                aria-label="Assumed Annual TSP Growth Rate before retirement"
                placeholder="e.g., 7.0"
              />
               <span className="help-text">Avg. annual return while working.</span>
            </div>
            <div className="form-group compact">
              <label htmlFor="post-ret-growth">TSP Growth Rate (Post-Ret) (%):
                <div className="tooltip" style={{display: 'inline-block', marginLeft: '5px'}}>
                  <span className="tooltip-icon">?</span>
                  <span className="tooltip-text">Conservative 5.5-6.5% assumption accounts for lower risk allocation (more G/F funds), sequence of returns risk, and withdrawal impacts. Protects against running out of money if markets underperform early in retirement.</span>
                </div>
              </label>
              <input
                type="number" id="post-ret-growth"
                value={globalSettings.postRetirementGrowth}
                onChange={(e) => handleNumericChange('postRetirementGrowth', e.target.value)}
                 onBlur={(e) => handleBlurNumeric('postRetirementGrowth', e.target.value)}
                step="0.5" min="-10" max="25"
                className="number-input"
                aria-label="Assumed Annual TSP Growth Rate after retirement"
                placeholder="e.g., 5.0"
              />
               <span className="help-text">Avg. annual return after retirement.</span>
            </div>
          </div>

          <div className="form-row">
             <div className="form-group compact">
                <label htmlFor="tax-rate">Effective Tax Rate (%):</label>
                <input
                  type="number" id="tax-rate"
                  value={globalSettings.taxRate}
                  onChange={(e) => handleNumericChange('taxRate', e.target.value)}
                  onBlur={(e) => handleBlurNumeric('taxRate', e.target.value)}
                  step="0.5" min="0" max="100" /* Allow decimals */
                  className="number-input"
                  aria-label="Estimated Effective Tax Rate Percentage"
                   disabled={!globalSettings.showAfterTax}
                   placeholder="e.g., 22.0"
                />
                 <span className="help-text">Overall rate (Fed+State) for After-Tax view.</span>
              </div>
              <div className="form-group toggle-group compact">
                <label htmlFor="show-after-tax">
                   <input type="checkbox" id="show-after-tax"
                       checked={globalSettings.showAfterTax}
                       onChange={(e) => handleCheckboxChange('showAfterTax', e.target.checked)}
                       aria-label="Show calculated values after applying the effective tax rate"
                   />
                   Show After-Tax Values
                </label>
              </div>
          </div>

          <div className="form-row">
            <div className="form-group compact">
              <label htmlFor="end-year">End Projection Year:</label>
              <input
                type="number" id="end-year"
                value={globalSettings.endPensionYear}
                onChange={(e) => handleIntegerChange('endPensionYear', e.target.value)}
                 onBlur={(e) => handleBlurInteger('endPensionYear', e.target.value, currentYear + 40)}
                min={currentYear + 10}
                max={currentYear + 70}
                step="1"
                className="number-input"
                aria-label="Final year for the projection calculations"
                placeholder={currentYear + 40}
              />
               <span className="help-text">Last year shown in charts/tables.</span>
            </div>
          </div>
        </div>
      {!isEmbedded && (
        <SectionNavigation
          onNextSection={onNextSection}
          onPreviousSection={onPreviousSection}
          currentSectionIndex={currentSectionIndex}
          sectionNames={sectionNames}
        />
      )}
    </div>
  );
}

export default GlobalSettingsForm;